import { OverlayViewF, useGoogleMap } from '@react-google-maps/api'
import React, { MouseEventHandler, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'

import Clickable from 'components/Common/Clickable'
import { MapMarkerProps, panToIntoViewIfObstructed, pinOffSetter } from 'tripPlanner/components/TripMaps/utils'
import { TEARDROP_PIN_HEIGHT } from 'tripPlanner/components/TripMaps/MapElement/Pins/SelectedMapItemPin'
import SearchCustomPin from 'tripPlanner/components/TripMaps/MapElement/Pins/SearchCustomPin'
import MapInfoWindow from 'tripPlanner/components/TripMaps/MapElement/MapInfoWindow'
import MapSummaryCard from 'tripPlanner/components/TripMaps/SummaryCards'
import { MarkerItem } from 'tripPlanner/types/tripItem'

const StyledClickable = styled(Clickable)`
  position: absolute;
  left: 0;
  bottom: 0;
  transform: translateX(-50%);
`

const Container = styled.div`
  position: relative;
`

interface Props
  extends Required<
    Omit<MapMarkerProps, 'includeInMapBounds' | 'itemPlaceType' | 'zIndex' | 'onSelect' | 'mapElementType'>
  > {
  enableWindow?: boolean
  itemCardRef: React.MutableRefObject<HTMLDivElement | null>
  onSelect: (item: MarkerItem) => void
}

function SearchMapMarker({
  active,
  item,
  onDeselect,
  onSelect,
  position,
  itemCardRef,
}: Props) {
  const map = useGoogleMap()
  const windowRef = useRef<HTMLDivElement | null>(null)

  const onClick: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (active) {
        onDeselect()
      } else {
        onSelect(item)
      }
    },
    [active, item, onDeselect, onSelect],
  )

  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (active && map && windowRef.current) {
      // We should only really pan into position if its obstructed?
      panToIntoViewIfObstructed(map, position, itemCardRef.current)
    }
  }, [active, itemCardRef, map, position])

  return (
    <OverlayViewF
      zIndex={active ? 999 : -1}
      position={position}
      css={{ pointerEvents: 'none' }}
      getPixelPositionOffset={pinOffSetter}
      mapPaneName="overlayMouseTarget"
    >
      <Container ref={ref}>
        <StyledClickable onClick={onClick}>
          <SearchCustomPin isActive={active}/>
        </StyledClickable>

        <MapInfoWindow
          ref={windowRef}
          open={active}
          alignment="center"
          position="above"
          style={{ top: -TEARDROP_PIN_HEIGHT * 1.3 }}
        >
          <MapSummaryCard item={item} />
        </MapInfoWindow>
      </Container>
    </OverlayViewF>
  )
}

export default React.memo(SearchMapMarker)
